/**
 * DevisFacturerController — page /devis-facturer-edition.html (EPIC-051 / 51.5).
 *
 * Wizard de facturation à 3 étapes (§7.G) :
 *   1. Q1 « Acompte ? » — Oui/Non + type (pourcentage|montant) + valeur.
 *   2. Q2 « Jalons / situations ? » — Oui/Non + saisie des jalons (label,
 *      pourcentage, montant).
 *   3. Preview — liste ordonnée (advance_payment → milestones → settlement)
 *      avant `POST /v1/quotes/{id}/generate-invoices`.
 *
 * Cas particulier : si un `advance_payment` est déjà émis sur ce devis
 * (typiquement service d'encaissement §6 — pas encore livré), Q1 est passée
 * implicitement (« pas de nouvel acompte ») et le 1er jalon de Q2 est
 * pré-rempli read-only avec le pourcentage hérité de cet acompte existant.
 *
 * Vocabulaire UI strict (cf. CLAUDE.md) : pas de mention de NotaFacto, PDP,
 * Sweego, OCR dans les chaînes affichées.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const QUOTE_STATUS_LABELS = {
  draft: "brouillon",
  issued: "émis",
  sent: "envoyé",
  accepted: "accepté",
  refused: "refusé",
  partially_invoiced: "partiellement facturé",
  fully_invoiced: "facturé",
  expired: "expiré",
  archived: "archivé",
};

const KIND_LABELS = {
  standard: "Facture standard",
  advance_payment: "Acompte",
  milestone: "Jalon / situation",
  settlement: "Solde",
};

export default class DevisFacturerController extends BaseController {
  static targets = [
    "crumbQuoteLink",
    "errorBanner",
    "errorBannerMessage",
    "advanceLockedBanner",
    "advanceLockedHint",
    "step1",
    "step2",
    "step3",
    "cancelLink",
    "advanceNo",
    "advanceYes",
    "advanceBlock",
    "advanceTypePercent",
    "advanceTypeAmount",
    "advancePercentField",
    "advancePercent",
    "advancePercentError",
    "advanceAmountField",
    "advanceAmount",
    "advanceAmountError",
    "milestonesNo",
    "milestonesYes",
    "milestonesBlock",
    "milestonesBody",
    "distributeBtn",
    "milestonesCounter",
    "milestonesError",
    "step2NextBtn",
    "previewBody",
    "previewTotal",
    "submitError",
    "submitBtn",
    "recapNumber",
    "recapClient",
    "recapStatus",
    "recapTotal",
  ];

  connect() {
    this._quoteId = new URLSearchParams(window.location.search).get("quoteId");
    if (!this._quoteId) {
      this._showBannerError("Identifiant devis manquant dans l'URL.");
      return;
    }
    if (this.hasCancelLinkTarget) {
      this.cancelLinkTarget.setAttribute(
        "href",
        `/devis-detail.html?id=${encodeURIComponent(this._quoteId)}`,
      );
    }
    this._lockedAdvance = null;
    this._milestones = [];
    this.bootstrap();
  }

  async bootstrap() {
    try {
      const [quote, invoicesResp, paymentSettings] = await Promise.all([
        api.getQuote(this._quoteId),
        api.getQuoteInvoices(this._quoteId),
        api.getPaymentProviderSettings().catch(() => null),
      ]);
      this._quote = quote;
      const invoices = Array.isArray(invoicesResp?.invoices) ? invoicesResp.invoices : [];
      this._renderRecap(quote);
      this._guardAlreadyGenerated(invoices);
      this._detectLockedAdvance(invoices);
      this._prefillAdvanceDefault(paymentSettings);
      this._maybeHydrateClient(quote.clientId);
    } catch (error) {
      const msg = (error instanceof APIError && error.message)
        ? error.message
        : "Impossible de charger le devis.";
      this._showBannerError(msg);
    }
  }

  // ---------------------------------------------------------------------------
  // Boot helpers
  // ---------------------------------------------------------------------------

  _renderRecap(quote) {
    const number = quote.number ?? "Brouillon";
    if (this.hasCrumbQuoteLinkTarget) {
      this.crumbQuoteLinkTarget.textContent = number;
      this.crumbQuoteLinkTarget.setAttribute(
        "href",
        `/devis-detail.html?id=${encodeURIComponent(quote.id)}`,
      );
    }
    if (this.hasRecapNumberTarget) this.recapNumberTarget.textContent = number;
    if (this.hasRecapStatusTarget) {
      this.recapStatusTarget.textContent =
        QUOTE_STATUS_LABELS[quote.status] ?? quote.status ?? "—";
    }
    if (this.hasRecapTotalTarget) {
      this.recapTotalTarget.textContent = quote.amountInclVat
        ? `${this._formatMoney(quote.amountInclVat)} €`
        : "—";
    }
  }

  async _maybeHydrateClient(clientId) {
    if (!clientId || !this.hasRecapClientTarget) return;
    try {
      const client = await api.getClient(clientId);
      this.recapClientTarget.textContent = client.legalName ?? "—";
    } catch {
      this.recapClientTarget.textContent = "(client introuvable)";
    }
  }

  _guardAlreadyGenerated(invoices) {
    const live = invoices.filter((i) => i.status !== "archived" && i.status !== "cancelled");
    if (live.length === 0) return;
    const hasIssued = live.some((i) => i.status !== "draft");
    if (hasIssued) {
      this._showBannerError(
        "Un lot de factures existe déjà pour ce devis. Retour à la fiche devis pour gérer les factures en cours.",
      );
      if (this.hasStep1Target) this.step1Target.hidden = true;
      return;
    }
    this._showBannerError(
      "Un lot de brouillons existe déjà pour ce devis. Recommencez le découpage depuis la fiche devis avant de relancer le wizard.",
    );
    if (this.hasStep1Target) this.step1Target.hidden = true;
  }

  _detectLockedAdvance(invoices) {
    const issuedAdvance = invoices.find(
      (i) => i.kind === "advance_payment" && i.status !== "draft" && i.status !== "archived" && i.status !== "cancelled",
    );
    if (!issuedAdvance) return;
    const ttc = Number(this._quote?.amountInclVat) || 0;
    const amount = Number(issuedAdvance.amountInclVat) || 0;
    const percent = ttc > 0 ? (amount / ttc) * 100 : 0;
    this._lockedAdvance = {
      amount,
      percent,
      label: issuedAdvance.milestoneLabel || issuedAdvance.number || "Acompte versé",
      number: issuedAdvance.number,
    };
    if (this.hasAdvanceLockedBannerTarget) {
      this.advanceLockedBannerTarget.hidden = false;
    }
    if (this.hasAdvanceLockedHintTarget) {
      this.advanceLockedHintTarget.textContent =
        `${this._formatMoney(amount)} € — soit ${this._formatMoney(percent)} %`;
    }
    if (this.hasAdvanceNoTarget) this.advanceNoTarget.checked = true;
    if (this.hasAdvanceYesTarget) this.advanceYesTarget.disabled = true;
    if (this.hasAdvanceBlockTarget) this.advanceBlockTarget.hidden = true;
  }

  _prefillAdvanceDefault(settings) {
    if (!settings || !this.hasAdvancePercentTarget) return;
    const def = settings.quoteDepositPct;
    if (def && Number.isFinite(Number(def))) {
      this.advancePercentTarget.value = Number(def).toString();
    }
  }

  // ---------------------------------------------------------------------------
  // Step 1 — Acompte
  // ---------------------------------------------------------------------------

  onAdvanceToggle() {
    const yes = this.hasAdvanceYesTarget && this.advanceYesTarget.checked;
    if (this.hasAdvanceBlockTarget) this.advanceBlockTarget.hidden = !yes;
  }

  onAdvanceTypeChange() {
    const percent = this.hasAdvanceTypePercentTarget && this.advanceTypePercentTarget.checked;
    if (this.hasAdvancePercentFieldTarget) this.advancePercentFieldTarget.hidden = !percent;
    if (this.hasAdvanceAmountFieldTarget) this.advanceAmountFieldTarget.hidden = percent;
  }

  _collectAdvance() {
    if (!this.hasAdvanceYesTarget || !this.advanceYesTarget.checked) return null;
    const type = this.hasAdvanceTypeAmountTarget && this.advanceTypeAmountTarget.checked
      ? "amount"
      : "percent";
    const raw = type === "amount"
      ? this.advanceAmountTarget?.value
      : this.advancePercentTarget?.value;
    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) return null;
    return { type, value };
  }

  _validateAdvance() {
    if (this.hasAdvancePercentErrorTarget) this.advancePercentErrorTarget.hidden = true;
    if (this.hasAdvanceAmountErrorTarget) this.advanceAmountErrorTarget.hidden = true;
    if (!this.hasAdvanceYesTarget || !this.advanceYesTarget.checked) return true;
    const type = this.hasAdvanceTypeAmountTarget && this.advanceTypeAmountTarget.checked
      ? "amount"
      : "percent";
    const ttc = Number(this._quote?.amountInclVat) || 0;
    if (type === "percent") {
      const v = Number(this.advancePercentTarget?.value);
      if (!Number.isFinite(v) || v <= 0 || v >= 100) {
        this._fieldError(this.advancePercentErrorTarget, "Saisissez un pourcentage entre 0,01 et 99,99.");
        return false;
      }
    } else {
      const v = Number(this.advanceAmountTarget?.value);
      if (!Number.isFinite(v) || v <= 0) {
        this._fieldError(this.advanceAmountErrorTarget, "Saisissez un montant strictement positif.");
        return false;
      }
      if (ttc > 0 && v >= ttc) {
        this._fieldError(this.advanceAmountErrorTarget, "L'acompte doit être strictement inférieur au TTC du devis.");
        return false;
      }
    }
    return true;
  }

  goToStep2() {
    if (!this._validateAdvance()) return;
    this._showStep(2);
    if (this._milestones.length === 0) {
      this._initMilestones();
    }
    this._refreshMilestonesUi();
  }

  backToStep1() {
    this._showStep(1);
  }

  // ---------------------------------------------------------------------------
  // Step 2 — Milestones
  // ---------------------------------------------------------------------------

  _initMilestones() {
    if (this._lockedAdvance) {
      this._milestones = [{
        readonly: true,
        nature: "percentage",
        label: this._lockedAdvance.label,
        percent: this._lockedAdvance.percent,
        amount: this._lockedAdvance.amount,
      }];
    } else {
      this._milestones = [];
    }
    this._milestones.push(this._newMilestone());
  }

  onMilestonesToggle() {
    const yes = this.hasMilestonesYesTarget && this.milestonesYesTarget.checked;
    if (this.hasMilestonesBlockTarget) this.milestonesBlockTarget.hidden = !yes;
    if (yes && this._milestones.length === 0) {
      this._initMilestones();
      if (this._milestones.length === 0) {
        this._milestones.push(this._newMilestone());
      }
    }
    this._refreshMilestonesUi();
  }

  addMilestone() {
    this._milestones.push(this._newMilestone());
    this._refreshMilestonesUi();
  }

  _newMilestone() {
    return {
      readonly: false,
      nature: "percentage",
      label: "",
      percent: null,
      amount: null,
      quantityLines: null,
    };
  }

  /**
   * Prépare lazy les lignes saisissables d'un jalon `quantity` à partir
   * du devis (1 entrée par ligne du devis, incluse par défaut à la qté
   * d'origine). Conserve l'état entre toggles si déjà initialisé.
   */
  _ensureQuantityLines(milestone) {
    if (milestone.quantityLines !== null) return;
    const quoteLines = Array.isArray(this._quote?.lines) ? this._quote.lines : [];
    milestone.quantityLines = quoteLines.map((line) => ({
      quoteLineIndex: Number(line.lineIndex),
      label: line.label ?? "",
      reference: line.reference ?? "",
      unit: line.unit ?? "u",
      qtyOrigin: Number(line.quantity) || 0,
      qtyDone: Number(line.quantity) || 0,
      priceExclVat: Number(line.priceExclVat) || 0,
      vatRate: line.vatRate === null || line.vatRate === undefined
        ? null
        : Number(line.vatRate),
      included: true,
    }));
    this._recomputeQuantityAmount(milestone);
  }

  _recomputeQuantityAmount(milestone) {
    const lines = milestone.quantityLines ?? [];
    let amount = 0;
    for (const line of lines) {
      if (!line.included) continue;
      const qty = Number(line.qtyDone) || 0;
      if (qty <= 0) continue;
      const excl = Math.round(qty * line.priceExclVat * 100) / 100;
      const vatRate = line.vatRate;
      const vat = vatRate === null ? 0 : Math.round(excl * vatRate) / 100;
      amount += excl + vat;
    }
    milestone.amount = Math.round(amount * 100) / 100;
    const ttc = Number(this._quote?.amountInclVat) || 0;
    milestone.percent = ttc > 0 ? (milestone.amount / ttc) * 100 : null;
  }

  /**
   * Vide le pourcentage / montant des jalons saisissables de type
   * « pourcentage » pour repartir d'une base propre — typiquement avant un
   * « Répartir la somme restante » qui ré-équilibre alors également tous les
   * jalons. Les lignes read-only (acompte hérité) et les jalons « situation »
   * (montant calculé depuis les quantités) sont préservés.
   */
  resetMilestonePercents() {
    for (const m of this._milestones) {
      if (m.readonly) continue;
      if (m.nature !== "percentage") continue;
      m.percent = null;
      m.amount = null;
    }
    this._refreshMilestonesUi();
  }

  distributeRemaining() {
    const ttc = Number(this._quote?.amountInclVat) || 0;
    if (ttc <= 0) return;
    const target = ttc - this._computeAdvanceAmount();
    const sumMilestones = this._milestones
      .filter((m) => !m.readonly)
      .reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
    const remaining = target - sumMilestones;
    if (remaining <= 0) return;
    const emptyIdx = [];
    for (let i = 0; i < this._milestones.length; i++) {
      const m = this._milestones[i];
      if (m.readonly) continue;
      if (m.nature !== "percentage") continue;
      if (!Number.isFinite(Number(m.amount)) || Number(m.amount) <= 0) {
        emptyIdx.push(i);
      }
    }
    if (emptyIdx.length === 0) return;
    // +1 pour la ligne « Solde » (recipient implicite, auto-calculée
    // comme reliquat par _computeSettlementAmount).
    const recipients = emptyIdx.length + 1;
    const each = Math.floor((remaining / recipients) * 100) / 100;
    emptyIdx.forEach((idx) => {
      this._milestones[idx].amount = each;
      this._milestones[idx].percent = (each / ttc) * 100;
    });
    this._refreshMilestonesUi();
  }

  _refreshMilestonesUi() {
    if (!this.hasMilestonesBodyTarget) return;
    this._syncSyntheticAdvanceRow();
    while (this.milestonesBodyTarget.firstChild) {
      this.milestonesBodyTarget.removeChild(this.milestonesBodyTarget.firstChild);
    }
    const ttc = Number(this._quote?.amountInclVat) || 0;
    this._milestones.forEach((m, idx) => {
      if (m.readonly) {
        const fragment = this.cloneTemplate("tpl-milestone-row-readonly");
        this.fillSlots(fragment, {
          index: String(idx + 1),
          percent: Number.isFinite(Number(m.percent)) ? `${this._formatMoney(m.percent)} %` : "—",
          amount: Number.isFinite(Number(m.amount)) ? `${this._formatMoney(m.amount)} €` : "—",
        });
        this.milestonesBodyTarget.appendChild(fragment);
        return;
      }

      const fragment = this.cloneTemplate("tpl-milestone-row");
      this.fillSlots(fragment, { index: String(idx + 1) });
      const row = fragment.querySelector("[data-milestone-row]");
      if (!row) return;

      const natureSelect = row.querySelector("[data-milestone-nature]");
      const labelInput = row.querySelector("[data-milestone-label]");
      const percentInput = row.querySelector("[data-milestone-percent]");
      const amountInput = row.querySelector("[data-milestone-amount]");
      const removeBtn = row.querySelector("[data-milestone-remove]");

      const isQuantity = m.nature === "quantity";

      if (natureSelect) {
        natureSelect.value = m.nature ?? "percentage";
        natureSelect.addEventListener("change", (e) => {
          this._milestones[idx].nature = e.target.value === "quantity" ? "quantity" : "percentage";
          if (this._milestones[idx].nature === "quantity") {
            this._ensureQuantityLines(this._milestones[idx]);
          }
          this._refreshMilestonesUi();
        });
      }
      if (labelInput) {
        labelInput.value = m.label ?? "";
        labelInput.addEventListener("input", (e) => {
          this._milestones[idx].label = e.target.value;
        });
      }
      if (percentInput) {
        percentInput.value = Number.isFinite(Number(m.percent))
          ? this._formatNumber(m.percent)
          : "";
        percentInput.disabled = isQuantity;
        if (!isQuantity) {
          percentInput.addEventListener("input", (e) => {
            const v = Number(e.target.value);
            if (Number.isFinite(v)) {
              this._milestones[idx].percent = v;
              if (ttc > 0) {
                this._milestones[idx].amount = Math.round((ttc * v) / 100 * 100) / 100;
                if (amountInput) amountInput.value = this._formatNumber(this._milestones[idx].amount);
              }
            } else {
              this._milestones[idx].percent = null;
              this._milestones[idx].amount = null;
              if (amountInput) amountInput.value = "";
            }
            this._refreshSoldeRow();
            this._refreshCounter();
          });
        }
      }
      if (amountInput) {
        amountInput.value = Number.isFinite(Number(m.amount))
          ? this._formatNumber(m.amount)
          : "";
        amountInput.disabled = isQuantity;
        if (!isQuantity) {
          amountInput.addEventListener("input", (e) => {
            const v = Number(e.target.value);
            if (Number.isFinite(v)) {
              this._milestones[idx].amount = v;
              if (ttc > 0) {
                this._milestones[idx].percent = (v / ttc) * 100;
                if (percentInput) percentInput.value = this._formatNumber(this._milestones[idx].percent);
              }
            } else {
              this._milestones[idx].amount = null;
              this._milestones[idx].percent = null;
              if (percentInput) percentInput.value = "";
            }
            this._refreshSoldeRow();
            this._refreshCounter();
          });
        }
      }
      if (removeBtn) {
        removeBtn.addEventListener("click", () => {
          this._milestones.splice(idx, 1);
          this._refreshMilestonesUi();
        });
      }
      this.milestonesBodyTarget.appendChild(fragment);

      if (isQuantity) {
        this._ensureQuantityLines(m);
        this._appendQuantityBlock(idx, m);
      }
    });
    this._appendSoldeRow();
    this._refreshCounter();
  }

  /**
   * Montant TTC de l'acompte du lot (saisi à l'étape 1 ou hérité d'un
   * acompte déjà émis). Sert de base au calcul du target et du solde.
   */
  _computeAdvanceAmount() {
    const ttc = Number(this._quote?.amountInclVat) || 0;
    const advance = this._collectAdvance();
    if (advance) {
      return advance.type === "percent"
        ? Math.round((ttc * advance.value) / 100 * 100) / 100
        : Number(advance.value) || 0;
    }
    return this._lockedAdvance ? Number(this._lockedAdvance.amount) || 0 : 0;
  }

  /**
   * EPIC-099 / 99.49 — D1 : la ligne « Solde » (settlement) absorbe le
   * reliquat : `solde = TTC − acompte − Σ jalons intermédiaires`. Dérivé
   * en direct, jamais une entrée du tableau `_milestones`.
   */
  _computeSettlementAmount() {
    const ttc = Number(this._quote?.amountInclVat) || 0;
    const target = ttc - this._computeAdvanceAmount();
    const sumMilestones = this._milestones
      .filter((m) => !m.readonly)
      .reduce((s, m) => s + (Number(m.amount) || 0), 0);
    return Math.round((target - sumMilestones) * 100) / 100;
  }

  /**
   * Ajoute la ligne « Solde » épinglée (non supprimable, lecture seule) en
   * pied du tableau des jalons. Recalculée à chaque rendu (édition, ajout,
   * suppression, répartition, remise à zéro). EPIC-099 / 99.49.
   */
  _appendSoldeRow() {
    if (!this.hasMilestonesBodyTarget) return;
    const ttc = Number(this._quote?.amountInclVat) || 0;
    const solde = this._computeSettlementAmount();
    const percent = ttc > 0 ? (solde / ttc) * 100 : 0;
    const fragment = this.cloneTemplate("tpl-milestone-solde-row");
    this.fillSlots(fragment, {
      percent: `${this._formatMoney(percent)} %`,
      amount: `${this._formatMoney(solde)} €`,
    });
    this.milestonesBodyTarget.appendChild(fragment);
  }

  /**
   * Synchronise une entrée readonly synthétique en tête de `_milestones`
   * représentant l'acompte saisi à l'étape 1. Cohabite avec le verrou (déjà
   * géré par `_initMilestones`) : aucun ajout si verrou présent. Marquée
   * `_synthetic: true` pour pouvoir être resync à chaque rafraîchissement
   * (utilisateur qui revient à l'étape 1 et modifie Q1). Filtre `!readonly`
   * partout en aval = pas d'effet sur sommes/distribution/payload.
   */
  _syncSyntheticAdvanceRow() {
    const existing = this._milestones[0]?._synthetic ? this._milestones[0] : null;
    if (this._lockedAdvance) {
      if (existing) this._milestones.shift();
      return;
    }
    const advance = this._collectAdvance();
    if (!advance) {
      if (existing) this._milestones.shift();
      return;
    }
    const ttc = Number(this._quote?.amountInclVat) || 0;
    const amount = this._computeAdvanceAmount();
    const percent = ttc > 0 ? (amount / ttc) * 100 : 0;
    const row = {
      readonly: true,
      _synthetic: true,
      nature: "percentage",
      label: "Acompte",
      percent,
      amount,
    };
    if (existing) {
      this._milestones[0] = row;
    } else {
      this._milestones.unshift(row);
    }
  }

  /**
   * Met à jour en place les cellules % et montant de la ligne solde, sans
   * re-render tout le tableau (sinon les inputs en cours de saisie perdent
   * le focus). Appelé sur chaque saisie d'un jalon.
   */
  _refreshSoldeRow() {
    if (!this.hasMilestonesBodyTarget) return;
    const row = this.milestonesBodyTarget.querySelector("[data-milestone-solde='true']");
    if (!row) return;
    const ttc = Number(this._quote?.amountInclVat) || 0;
    const solde = this._computeSettlementAmount();
    const percent = ttc > 0 ? (solde / ttc) * 100 : 0;
    const percentCell = row.querySelector("[data-slot='percent']");
    const amountCell = row.querySelector("[data-slot='amount']");
    if (percentCell) percentCell.textContent = `${this._formatMoney(percent)} %`;
    if (amountCell) amountCell.textContent = `${this._formatMoney(solde)} €`;
  }

  _appendQuantityBlock(milestoneIdx, milestone) {
    const fragment = this.cloneTemplate("tpl-milestone-quantity-block");
    const tbody = fragment.querySelector("[data-milestone-quantity-body]");
    const totalCell = fragment.querySelector("[data-milestone-quantity-total]");
    if (!tbody || !totalCell) return;

    const refreshTotalCell = () => {
      totalCell.textContent = Number.isFinite(Number(milestone.amount))
        ? `${this._formatMoney(milestone.amount)} €`
        : "—";
    };

    milestone.quantityLines.forEach((line, lineIdx) => {
      const lineFragment = this.cloneTemplate("tpl-milestone-quantity-line");
      const ref = line.reference ? ` · ${line.reference}` : "";
      this.fillSlots(lineFragment, {
        label: line.label,
        reference: ref,
        qtyOrigin: `${this._formatNumber(line.qtyOrigin)} ${line.unit}`,
        priceExcl: `${this._formatMoney(line.priceExclVat)} €`,
        vatRate: line.vatRate === null ? "Franchise" : `${this._formatNumber(line.vatRate)} %`,
        lineTotal: `${this._formatMoney(this._computeQuantityLineTotal(line))} €`,
      });

      const lineRow = lineFragment.querySelector("[data-milestone-quantity-line]");
      const totalSlot = lineFragment.querySelector("[data-slot='lineTotal']");
      const includeInput = lineFragment.querySelector("[data-quantity-include]");
      const qtyInput = lineFragment.querySelector("[data-quantity-input]");

      const refreshLineUi = () => {
        const target = this._milestones[milestoneIdx].quantityLines[lineIdx];
        if (qtyInput) qtyInput.disabled = !target.included;
        if (lineRow) lineRow.style.opacity = target.included ? "1" : "0.55";
        if (totalSlot) {
          totalSlot.textContent = `${this._formatMoney(this._computeQuantityLineTotal(target))} €`;
        }
      };

      if (includeInput) {
        includeInput.checked = line.included;
        includeInput.addEventListener("change", (e) => {
          this._milestones[milestoneIdx].quantityLines[lineIdx].included = e.target.checked;
          this._recomputeQuantityAmount(this._milestones[milestoneIdx]);
          refreshLineUi();
          refreshTotalCell();
          this._refreshCounter();
        });
      }
      if (qtyInput) {
        qtyInput.value = this._formatNumber(line.qtyDone);
        qtyInput.max = String(line.qtyOrigin);
        qtyInput.disabled = !line.included;
        qtyInput.addEventListener("input", (e) => {
          const v = Number(e.target.value);
          this._milestones[milestoneIdx].quantityLines[lineIdx].qtyDone =
            Number.isFinite(v) ? Math.max(0, Math.min(v, line.qtyOrigin)) : 0;
          this._recomputeQuantityAmount(this._milestones[milestoneIdx]);
          refreshLineUi();
          refreshTotalCell();
          this._refreshCounter();
        });
      }
      refreshLineUi();
      tbody.appendChild(lineFragment);
    });
    refreshTotalCell();
    this.milestonesBodyTarget.appendChild(fragment);
  }

  _computeQuantityLineTotal(line) {
    if (!line.included) return 0;
    const qty = Number(line.qtyDone) || 0;
    if (qty <= 0) return 0;
    const excl = Math.round(qty * line.priceExclVat * 100) / 100;
    const vatRate = line.vatRate;
    const vat = vatRate === null ? 0 : Math.round(excl * vatRate) / 100;
    return Math.round((excl + vat) * 100) / 100;
  }

  _refreshCounter() {
    if (!this.hasMilestonesCounterTarget) return;
    const target = (Number(this._quote?.amountInclVat) || 0) - this._computeAdvanceAmount();
    const sumMilestones = this._milestones
      .filter((m) => !m.readonly)
      .reduce((s, m) => s + (Number(m.amount) || 0), 0);
    const solde = this._computeSettlementAmount();
    const count = this._milestones.filter((m) => !m.readonly).length;
    const noun = count > 1 ? "jalons" : "jalon";
    this.milestonesCounterTarget.textContent =
      `${this._formatMoney(sumMilestones)} € de jalons sur ${this._formatMoney(target)} € · `
      + `${count} ${noun} · solde de ${this._formatMoney(solde)} € complété par la facture de solde`;
    if (this.hasStep2NextBtnTarget && this.hasMilestonesYesTarget && this.milestonesYesTarget.checked) {
      // EPIC-099 / 99.49 — D2 : anti-dépassement seul (solde ≥ 0) + ≥ 1 jalon.
      this.step2NextBtnTarget.disabled = solde < -0.01 || count < 1;
    } else if (this.hasStep2NextBtnTarget) {
      this.step2NextBtnTarget.disabled = false;
    }
  }

  goToStep3() {
    if (!this._validateMilestones()) return;
    this._renderPreview();
    this._showStep(3);
  }

  backToStep2() {
    this._showStep(2);
  }

  _validateMilestones() {
    if (this.hasMilestonesErrorTarget) this.milestonesErrorTarget.hidden = true;
    if (!this.hasMilestonesYesTarget || !this.milestonesYesTarget.checked) return true;
    if (this._milestones.filter((m) => !m.readonly).length === 0) {
      this._fieldError(this.milestonesErrorTarget, "Ajoutez au moins un jalon ou choisissez « Non ».");
      return false;
    }
    for (const m of this._milestones) {
      if (m.readonly) continue;
      if (m.nature === "quantity") {
        const lines = Array.isArray(m.quantityLines) ? m.quantityLines : [];
        const hasIncludedLine = lines.some((l) => l.included && Number(l.qtyDone) > 0);
        if (!hasIncludedLine) {
          this._fieldError(this.milestonesErrorTarget, "Chaque situation doit inclure au moins une ligne avec une quantité.");
          return false;
        }
        for (const l of lines) {
          if (!l.included) continue;
          if (Number(l.qtyDone) > Number(l.qtyOrigin) + 0.0001) {
            this._fieldError(this.milestonesErrorTarget, "Une quantité saisie dépasse la quantité du devis.");
            return false;
          }
        }
      }
      if (!Number.isFinite(Number(m.amount)) || Number(m.amount) <= 0) {
        this._fieldError(this.milestonesErrorTarget, "Chaque jalon doit avoir un montant strictement positif.");
        return false;
      }
    }
    // EPIC-099 / 99.49 — D2 : anti-dépassement seul. La ligne « Solde »
    // absorbe le reliquat (`solde = TTC − acompte − Σ jalons ≥ 0`) ; plus
    // aucune contrainte Σ=100 % (ni percentage, ni situation).
    if (this._computeSettlementAmount() < -0.01) {
      this._fieldError(
        this.milestonesErrorTarget,
        "Acompte + jalons dépasse le TTC du devis. Réduisez un montant.",
      );
      return false;
    }
    return true;
  }

  // ---------------------------------------------------------------------------
  // Step 3 — Preview + submit
  // ---------------------------------------------------------------------------

  _renderPreview() {
    if (!this.hasPreviewBodyTarget) return;
    while (this.previewBodyTarget.firstChild) {
      this.previewBodyTarget.removeChild(this.previewBodyTarget.firstChild);
    }
    const ttc = Number(this._quote?.amountInclVat) || 0;
    const rows = [];
    const advance = this._collectAdvance();
    const hasMilestones = this.hasMilestonesYesTarget && this.milestonesYesTarget.checked && this._milestones.length > 0;

    if (this._lockedAdvance) {
      rows.push({
        kind: "advance_payment",
        label: this._lockedAdvance.label,
        amount: this._lockedAdvance.amount,
        note: "déjà émis — non recréé",
      });
    } else if (advance) {
      const amt = advance.type === "percent"
        ? Math.round((ttc * advance.value) / 100 * 100) / 100
        : Number(advance.value);
      rows.push({
        kind: "advance_payment",
        label: `Acompte (${advance.type === "percent" ? `${this._formatMoney(advance.value)} %` : `${this._formatMoney(amt)} €`})`,
        amount: amt,
      });
    }

    if (hasMilestones) {
      const editableMilestones = this._milestones.filter((m) => !m.readonly);
      editableMilestones.forEach((m, i) => {
        const natureLabel = m.nature === "quantity" ? "Situation" : "Jalon";
        rows.push({
          kind: "milestone",
          natureLabel,
          label: m.label || `${natureLabel} ${i + 1}`,
          amount: Number(m.amount) || 0,
        });
      });
    } else if (!advance && !this._lockedAdvance) {
      rows.push({
        kind: "standard",
        label: "Facture unique (100 % du devis)",
        amount: ttc,
      });
    }

    const hasOther = rows.length > 0;
    if (hasOther && (advance || this._lockedAdvance || hasMilestones) && !(rows.length === 1 && rows[0].kind === "standard")) {
      const advanceAmt = this._lockedAdvance
        ? Number(this._lockedAdvance.amount) || 0
        : (advance
          ? (advance.type === "percent" ? (ttc * advance.value) / 100 : Number(advance.value))
          : 0);
      const milestonesAmt = hasMilestones
        ? this._milestones.filter((m) => !m.readonly).reduce((s, m) => s + (Number(m.amount) || 0), 0)
        : 0;
      const settlementAmt = Math.round((ttc - advanceAmt - milestonesAmt) * 100) / 100;
      // EPIC-099 / 99.49 — D1 : la facture de solde est toujours présentée
      // (elle absorbe le reliquat), y compris quand le solde vaut 0.
      rows.push({
        kind: "settlement",
        label: "",
        amount: settlementAmt,
      });
    }

    rows.forEach((r, i) => {
      const fragment = this.cloneTemplate("tpl-preview-row");
      const kindLabel = r.natureLabel ?? (KIND_LABELS[r.kind] ?? r.kind);
      this.fillSlots(fragment, {
        order: String(i + 1),
        kind: kindLabel,
        label: r.note ? `${r.label} — ${r.note}` : r.label,
        amount: `${this._formatMoney(r.amount)} €`,
      });
      this.previewBodyTarget.appendChild(fragment);
    });

    if (this.hasPreviewTotalTarget) {
      const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
      this.previewTotalTarget.textContent = `${this._formatMoney(total)} €`;
    }
  }

  async submit() {
    if (this.hasSubmitErrorTarget) this.submitErrorTarget.hidden = true;
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = true;
    const advance = this._collectAdvance();
    const hasMilestones = this.hasMilestonesYesTarget && this.milestonesYesTarget.checked;
    const milestones = hasMilestones
      ? this._milestones
        .filter((m) => !m.readonly)
        .map((m) => {
          if (m.nature === "quantity") {
            return {
              label: m.label || "Situation",
              milestoneNature: "quantity",
              lines: (m.quantityLines ?? [])
                .filter((l) => l.included && Number(l.qtyDone) > 0)
                .map((l) => ({
                  quoteLineIndex: l.quoteLineIndex,
                  quantity: Number(l.qtyDone),
                })),
            };
          }
          return {
            label: m.label || "Jalon",
            milestoneNature: "percentage",
            amount: Number(m.amount),
          };
        })
      : null;
    const payload = {
      advance: advance ?? null,
      milestones: milestones && milestones.length > 0 ? milestones : null,
    };
    try {
      await api.generateQuoteInvoices(this._quoteId, payload);
      this.showSuccess("Brouillons générés.");
      window.location.assign(`/devis-detail.html?id=${encodeURIComponent(this._quoteId)}`);
    } catch (error) {
      const code = error instanceof APIError ? error.data?.error?.code : null;
      let msg = (error instanceof APIError && error.message)
        ? error.message
        : "Erreur lors de la génération des factures.";
      if (code === "quote_overflow" || code === "invoice_overflow") {
        msg = "Le total dépasserait le montant du devis. Réduisez l'acompte ou un jalon.";
      } else if (code === "quote_locked_existing_invoices") {
        msg = "Un lot de factures existe déjà. Recommencez le découpage depuis la fiche devis.";
      }
      this._fieldError(this.submitErrorTarget, msg);
      if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Step display helpers
  // ---------------------------------------------------------------------------

  _showStep(n) {
    if (this.hasStep1Target) this.step1Target.hidden = n !== 1;
    if (this.hasStep2Target) this.step2Target.hidden = n !== 2;
    if (this.hasStep3Target) this.step3Target.hidden = n !== 3;
    if (n === 1 && this.hasStep1Target) this.step1Target.focus?.();
    if (n === 2 && this.hasStep2Target) this.step2Target.focus?.();
    if (n === 3 && this.hasStep3Target) this.step3Target.focus?.();
  }

  _showBannerError(msg) {
    if (this.hasErrorBannerTarget) {
      this.errorBannerTarget.hidden = false;
    }
    if (this.hasErrorBannerMessageTarget) {
      this.errorBannerMessageTarget.textContent = msg;
    }
  }

  _fieldError(target, msg) {
    if (!target) return;
    target.textContent = msg;
    target.hidden = false;
  }

  _formatMoney(raw) {
    const num = Number(raw);
    if (!Number.isFinite(num)) return "—";
    return num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  _formatNumber(raw) {
    const num = Number(raw);
    if (!Number.isFinite(num)) return "";
    return (Math.round(num * 100) / 100).toString();
  }
}
